import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { MetaTags } from "react-meta-tags";
import { useNavigate, Link } from "react-router-dom";
import "../styles/loginpage.css"; // existing stylesheet
import siteLogo from "../images/new_logo.png"; 

function passwdStrength(password) {
  // returns score 0..4 and label
  let score = 0;
  if (!password) return { score: 0, label: "Too short" };
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[\W_]/.test(password)) score++;
  const label = ["Very weak", "Weak", "Moderate", "Strong", "Excellent"][Math.min(score,4)];
  return { score, label };
}

const ChangePasswordPage = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [changepasserror, setChangePassError] = useState(false);
  const [changepassmessage, setChangePassMessage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  let item = null;
  const itemStr = localStorage.getItem("isLogin");
  try {
    item = itemStr ? JSON.parse(itemStr) : null;
  } catch (_) {
    item = null;
  }
  const now = new Date();

  useEffect(() => {
    if (!itemStr || (item && item.expiry < now.getTime())) {
      navigate("/login");
    }
    // eslint-disable-next-line
  }, []);

  function Logout() {
    localStorage.removeItem("isLogin");
  }

  const strength = useMemo(() => passwdStrength(newPassword), [newPassword]);

  const validateForm = () => {
    if (!currentPassword) return { ok: false, msg: "Enter current password." };
    if (!newPassword) return { ok: false, msg: "Enter new password." };
    if (newPassword.length < 8 || newPassword.length > 16)
      return { ok: false, msg: "New password must be 8–16 characters." };
    if (newPassword !== confirmPass) return { ok: false, msg: "Passwords do not match." };
    return { ok: true };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setChangePassError(false);
    setChangePassMessage(false);

    const v = validateForm();
    if (!v.ok) {
      setChangePassError(true);
      return;
    }

    setSubmitting(true);

    try {
      const json = JSON.stringify({
        userid: item.userid,
        curpass: currentPassword,
        newpass: newPassword,
      });

      const headers = { "Content-Type": "application/json" };

      const res = await axios.post(
        "https://epolylearning.com/Services/polyquiz.svc/ChangeUserPassword",
        json,
        { headers }
      );

      const userData = res?.data;

      if (userData && userData.ChangeUserPasswordResult === "Password changed successfully.") {
        // success - reset form, logout and show modal
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPass("");
        setChangePassMessage(true);
        Logout();
        setShowModal(true);
      } else if (userData && userData.ChangeUserPasswordResult === "Current password is Wrong.") {
        setChangePassError(true);
      } else {
        // unexpected response - show generic error
        setChangePassError(true);
      }
    } catch (err) {
      console.error("Change password error:", err);
      setChangePassError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MetaTags>
        <title>Change User Password - PolyQuiz</title>
        <meta id="meta-description" name="description" />
      </MetaTags>

      <div className="cp-page">
        <div className="cp-hero">
          <div className="cp-card">
            <div className="cp-brand">
              <img src={siteLogo} alt="PolyQuizz Logo" className="cp-logo" />
              <div className="cp-brand-text">
                
                <h3 className="cp-title">Change Password</h3>
                <div className="cp-sub">Keep your account secure. Use a strong, unique password.</div>
              </div>
            </div>

            <div className="cp-form-wrap">
              <form className="cp-form" onSubmit={onSubmit} noValidate>
                <label className="cp-field">
                  <div className="cp-label">Current Password</div>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="cp-input"
                    placeholder="Enter current password"
                    required
                  />
                </label>

                <label className="cp-field">
                  <div className="cp-label">New Password</div>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="cp-input"
                    placeholder="Choose a new password (8–16 chars)"
                    minLength={8}
                    maxLength={16}
                    required
                  />

                  <div className="cp-strength">
                    <div className={`strength-bar s-${strength.score}`} />
                    <div className="strength-meta">
                      <span className="strength-label">{strength.label}</span>
                      <small className="strength-note">Use upper/lowercase, numbers & symbols</small>
                    </div>
                  </div>
                </label>

                <label className="cp-field">
                  <div className="cp-label">Confirm New Password</div>
                  <input
                    type="password"
                    value={confirmPass}
                    onChange={(e) => setConfirmPass(e.target.value)}
                    className="cp-input"
                    placeholder="Repeat new password"
                    required
                  />
                </label>

                <div style={{ marginTop: 12 }}>
                  <button type="submit" className="cp-submit-btn" disabled={submitting}>
                    {submitting ? "Processing…" : "Change Password"}
                  </button>
                </div>
              </form>

              {changepasserror && (
                <div className="cp-message cp-error" role="alert" style={{ marginTop: 12 }}>
                  <strong>Current password is wrong.</strong> Please try again.
                </div>
              )}

              {changepassmessage && (
                <div className="cp-message cp-success" role="status" style={{ marginTop: 12 }}>
                  <strong>Password changed successfully.</strong> You have been logged out — please{" "}
                  <Link to="/login" className="cp-link">sign in</Link> again.
                </div>
              )}
            </div>

           {/*  <div className="cp-footnote">
              <div>Need help? <Link to="/contact" className="cp-link">Contact support</Link></div>
              <div>
                <Link to="/profile" className="ghost-link">Back to profile</Link>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      {showModal && (
        <div className="cp-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="cp-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="cp-modal-icon">✓</div>
            <h4 className="cp-modal-title">Password changed</h4>
            <p className="cp-modal-text">Your password was changed successfully. Please sign in again to continue.</p>
            <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "center" }}>
              <Link to="/login" className="cp-submit-btn" onClick={() => setShowModal(false)}>Sign In</Link>
              <button className="cp-cancel-btn" onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChangePasswordPage;
